import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  memo
} from "react"
import { object, string, date, boolean } from "yup"
import {
  AlertIcon,
  Button,
  Stack,
  TextFieldProps,
  Typography
} from "@eventbrite/marmalade"
import {
  Formik,
  Form,
  FormikProps,
  FormikConfig,
  Field,
  useFormikContext
} from "formik"
import { withPrefix, PageProps } from "gatsby"
import { useDispatch, useSelector } from "react-redux"

// components
import { FormGating, FORM_ACCESS_CONFIG } from "../AuthWrapper/FormGating"
import { TextField } from "../Marmalade/TextField/TextField"
import { TextArea } from "../Marmalade/TextArea/TextArea"
import { SelectField } from "../Marmalade/SelectField/SelectField"
import { Layout } from "../Layout/Layout"
import { InlineAlert } from "../Marmalade/InlineAlert/InlineAlert"
import { BackCrumb } from "../Breadcrumbs/BackCrumb"
import PhoneInputField from "../Marmalade/PhoneInputField/PhoneInputField"
// redux
import { setInlineAlertProps } from "../../store/slices/alert.slice"

// utils
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../utils/contentful/render_rich_text"
import { renderEnhancedRichText } from "../../utils/contentful/enhanced-rich-text-renderer"
import { detectCountryCodeFromTLD } from "../../utils/general_utils"
import { LoadingSpinner } from "../AuthWrapper/FormGating"
// types
import { PickedLayoutProps } from "../../pages/{ContentfulHelpArticle.node_locale}/contact-us"

// styles
import * as styles from "../RequestCallback/RequestCallback.module.scss"
import * as formStyles from "../ContactForm/ContactForm.module.scss"
import { RootState } from "../../store/store"
import { UserProfile } from "../../store/slices/user_profile.slice"
import { Checkbox } from "../Marmalade/Checkbox/Checkbox"
interface ApiResponse<T> {
  result: T
  statusCode: number
  message?: string
}

export type MicroCopy = {
  raw: string
}

export type FormButtonMicroCopySet = {
  buttonBack: MicroCopy
  buttonSend: MicroCopy
}

export type Values = Record<string, string | boolean>

export type Options = {
  label: string
  value: string
}

export type RoutingSubtopic = {
  routingSubtopicSelection: string
  routingSubtopicSalesforceApiName: string
}

export type DropdownMenuOptions = {
  routingTopicInternalTitle: string
  routingTopicSelection: string
  routingTopicSalesforceApiName: string
  routingTopicChildItems: RoutingSubtopic[]
}

export type HandleTopicSelection = (
  selection: string,
  options: DropdownMenuOptions[]
) => void

export type HandleCountryOfViolationSelection = (
  selection: string,
  euCountries: readonly string[]
) => void

export type ContactFormContent =
  Queries.CommonIssuesAndQuestionsQuery["contactForms"]["edges"][0]["node"]

export type Props = {
  handleBackBtnClick: (removeQueryParam?: boolean) => void
  setCaseId: React.Dispatch<React.SetStateAction<string>>
  setCallbackFormSubmitted?: React.Dispatch<React.SetStateAction<boolean>>
  layoutProps: PickedLayoutProps
  gatsbyPageProps: PageProps
  contactForms: Queries.CommonIssuesAndQuestionsQuery["contactForms"]
  nodeLocale: string
  backCrumbTitle?: string
}

// This component is only necessary temporarily until a new form is created to handle non-eu region submissions.
// This component will be removed once the new form is created. sd-1838
const EU_COUNTRIES = [
  "Austria - AT",
  "Belgium - BE",
  "Bulgaria - BG",
  "Croatia - HR",
  "Cyprus - CY",
  "Czech Republic - CZ",
  "Denmark - DK",
  "Estonia - EE",
  "Finland - FI",
  "France - FR",
  "Germany - DE",
  "Greece - GR",
  "Hungary - HU",
  "Ireland - IE",
  "Italy - IT",
  "Latvia - LV",
  "Lithuania - LT",
  "Luxembourg - LX",
  "Malta - MT",
  "Netherlands - NL",
  "Poland - PL",
  "Portugal - PT",
  "Romania - RO",
  "Slovakia - SK",
  "Slovenia - SI",
  "Spain - ES",
  "Sweden - SE"
] as const

const CALLBACK_TOPIC_OPTIONS = [
  { label: "Select a topic", value: "" },
  { label: "My events", value: "my_events" },
  { label: "Finance", value: "finance" },
  { label: "Orders and attendees", value: "orders_and_attendees" },
  { label: "Marketing", value: "marketing" },
  { label: "Account", value: "account" },
  { label: "Packages and plans", value: "packages_and_plans" },
  { label: "Trust and safety", value: "trust_and_safety" },
  { label: "None of these", value: "none_of_these" }
]

// Memoized utility functions
const getDomainInfo = () => {
  if (typeof window === "undefined") return { domain: "", tld: "" }

  const fullDomain = window.location.hostname
  if (fullDomain === "localhost" || fullDomain === "dev.evbqa.com") {
    return { domain: "evbqa", tld: "com" }
  }

  const [, domain, tld1, tld2] = fullDomain.split(".")
  return {
    domain,
    tld: tld2 || tld1
  }
}

const getFormParameterValue = () => {
  if (typeof window === "undefined") return ""

  const queryParams = new URLSearchParams(window.location.search)
  const val = queryParams.get("form")
  return (val?.length ?? 0) > 0 ? val : ""
}

// Memoized custom validation component
const CustomValidation = memo(() => {
  const { errors, values, setErrors, validateForm, setFieldTouched } =
    useFormikContext<Values>()
  const prevCountryOfViolationRef = useRef<string>("")

  // Get country value from Formik values
  const countryOfViolation =
    (values["DSA Country of Violation (required)"] as string) || ""

  useEffect(() => {
    // Only update if countryOfViolation actually changed
    if (countryOfViolation !== prevCountryOfViolationRef.current) {
      prevCountryOfViolationRef.current = countryOfViolation

      if (EU_COUNTRIES.includes(countryOfViolation as any)) {
        setErrors({
          ...errors,
          "DSA Certify Checkbox (required)": "This field is required."
        })
      } else {
        // Remove the error if country is not EU
        const { "DSA Certify Checkbox (required)": _, ...rest } = errors
        setErrors(rest)
      }
      setFieldTouched("DSA Certify Checkbox (required)", true)
      // Only validate if we have values to validate
      if (
        values &&
        typeof values === "object" &&
        Object.keys(values).length > 0
      ) {
        validateForm(values)
      }
    }
  }, [countryOfViolation, setErrors, setFieldTouched, validateForm, values])

  return null
})

const FieldRenderer = memo(
  ({
    field,
    fieldRefs,
    handleChange,
    handleSelectMenuChange,
    handleCountryOfOriginChange,
    subtopics,
    errors,
    touched,
    isCallbackForm,
    formContent,
    callbackTopicOptions,
    tld,
    nodeLocale
  }: any) => {
    // Skip subject field for callback forms
    if (
      isCallbackForm &&
      (field?.internalTitle?.toLowerCase().includes("subject") ||
        field?.fieldName?.toLowerCase().includes("subject"))
    ) {
      return null
    }

    switch (field?.internalTitle) {
      case "Email address":
        return (
          <div ref={(el) => (fieldRefs.current[field.internalTitle] = el)}>
            <TextField
              key={field?.fieldName + field?.helperText}
              id={field?.fieldName}
              name={field?.internalTitle}
              label={field?.fieldName}
              required={field?.isRequired}
              helperText={field?.helperText}
              type="email"
              autoComplete="email"
            />
          </div>
        )

      case "Order number":
      case "Refund amount":
      case "Amount of purchase":
        return (
          <div ref={(el) => (fieldRefs.current[field.internalTitle] = el)}>
            <TextField
              key={field?.fieldName + field?.helperText}
              id={field?.fieldName}
              name={field?.internalTitle}
              label={field?.fieldName}
              required={field?.isRequired}
              helperText={field?.helperText}
              type="number"
            />
          </div>
        )

      case "Last 4 digits of card":
        return (
          <TextField
            key={field?.fieldName + field?.helperText}
            id={field?.fieldName}
            name={field?.internalTitle}
            label={field?.fieldName}
            required={field?.isRequired}
            helperText={field?.helperText}
            type="number"
            onChange={(value, name, event) => {
              if (event?.target?.value?.toString()?.length <= 4) {
                handleChange(event)
              }
            }}
          />
        )

      case "Phone number (Request callback form)":
        return (
          <div ref={(el) => (fieldRefs.current[field.internalTitle] = el)}>
            {isCallbackForm ? (
              <PhoneInputField
                numberFieldName={field?.internalTitle}
                countryFieldName={field?.fieldName}
                defaultCountryCode={detectCountryCodeFromTLD(tld, nodeLocale)}
              />
            ) : (
              <TextField
                key={field?.fieldName + field?.helperText}
                id={field?.fieldName}
                name={field?.internalTitle}
                label={field?.fieldName}
                required={field?.isRequired}
                helperText={field?.helperText}
                type="tel"
                autoComplete="tel"
              />
            )}
          </div>
        )
      case "Message (required)":
      case "Message (optional)":
      case "Message (required) - DSA RTE":
        if (isCallbackForm) {
          return (
            <>
              <div ref={(el) => (fieldRefs.current["Select a topic"] = el)}>
                <SelectField
                  id="select-a-topic"
                  name="Select a topic"
                  label="Select a topic"
                  required={true}
                  options={callbackTopicOptions}
                  helperText="Select the topic for your callback"
                />
              </div>
              <div ref={(el) => (fieldRefs.current[field.internalTitle] = el)}>
                <TextArea
                  id="description"
                  name={field?.internalTitle}
                  label="Description"
                  required={field?.isRequired}
                  placeholder="Please provide a brief description about your issue."
                  rows={4}
                  maxLength={100}
                />
              </div>
            </>
          )
        }
        return (
          <TextArea
            id={field?.fieldName}
            name={field?.internalTitle}
            label={field?.fieldName}
            required={field?.isRequired}
            helperText={field?.helperText}
            rows={3}
          />
        )

      case "Routing topics - Email": {
        const [topicHeader, subtopicHeader] = field.fieldName.split(" -- ")
        const topics = useMemo(
          () =>
            field.dropdownMenuOptions.map((option: any) => ({
              label: option.routingTopicSelection,
              value: option.routingTopicSalesforceApiName
            })),
          [field.dropdownMenuOptions]
        )

        return (
          <>
            <div ref={(el) => (fieldRefs.current[field.internalTitle] = el)}>
              <SelectField
                id={topicHeader}
                key={topicHeader + field?.helperText}
                name={topicHeader}
                label={topicHeader}
                options={topics}
                required={field?.isRequired}
                handleTopicSelection={handleSelectMenuChange}
                subtopicOptions={field?.dropdownMenuOptions}
              />
            </div>
            {subtopics?.length > 0 && (
              <div ref={(el) => (fieldRefs.current[field.internalTitle] = el)}>
                <SelectField
                  id={subtopicHeader}
                  key={subtopicHeader + field?.helperText}
                  name={subtopicHeader}
                  label={subtopicHeader}
                  options={subtopics}
                  required={true}
                  onChange={(value, name, event) => handleChange(event)}
                />
              </div>
            )}
          </>
        )
      }

      case "DSA Certify Checkbox (required)":
      case "DSA Report Certify Checkbox (required)":
        return (
          <Checkbox
            id={field?.fieldName}
            name={field?.internalTitle}
            labelText={field?.helperText}
          />
        )

      case "Event Date (required)":
        return (
          <div ref={(el) => (fieldRefs.current[field.internalTitle] = el)}>
            <TextField
              key={field?.fieldName + field?.helperText}
              id={field?.fieldName}
              name={field?.internalTitle}
              label={field?.fieldName}
              required={field?.isRequired}
              helperText={field?.helperText}
              placeholder="YYYY-MM-DD"
              onChange={(value, name, event) => {
                let input = event.target.value
                const regex = /^(\d{0,4})?(\d{0,2})?(\d{0,2})?$/

                input = input.replace(/\D/g, "")
                const match = input.match(regex)

                if (match) {
                  let formattedDate = ""

                  if (match[1]) {
                    formattedDate += match[1]
                    if (match[2]) {
                      formattedDate += `-${match[2]}`
                      if (match[3]) {
                        formattedDate += `-${match[3]}`
                      }
                    }
                  }

                  event.target.value = formattedDate
                  handleChange(event)
                }
              }}
            />
          </div>
        )

      case "DSA Reason for Appeal (required)":
      case "DSA-RTE Policy Category (required)":
      case "DSA Category of appeal (required)":
      case "DSA Policy Category (required)": {
        const dropdownOptions = useMemo(
          () =>
            field.dropdownMenuOptions.map((option: any) => ({
              label: option.routingTopicSelection,
              value: option.routingTopicSalesforceApiName
            })),
          [field.dropdownMenuOptions]
        )

        return (
          <div ref={(el) => (fieldRefs.current[field.internalTitle] = el)}>
            <SelectField
              id={field.fieldName}
              key={field.fieldName + field?.helperText}
              name={field.internalTitle}
              label={field.fieldName}
              options={dropdownOptions}
              required={field?.isRequired}
            />
          </div>
        )
      }

      case "DSA Country of Violation (required)": {
        const dropdownOptions = useMemo(
          () =>
            field.dropdownMenuOptions.map((option: any) => ({
              label: option.routingTopicSelection,
              value: option.routingTopicSalesforceApiName
            })),
          [field.dropdownMenuOptions]
        )

        return (
          <div ref={(el) => (fieldRefs.current[field.internalTitle] = el)}>
            <SelectField
              id={field.fieldName}
              key={field.fieldName + field?.helperText}
              name={field.internalTitle}
              label={field.fieldName}
              options={dropdownOptions}
              required={field?.isRequired}
              handleCountrySelection={handleCountryOfOriginChange}
              euCountries={EU_COUNTRIES}
            />
          </div>
        )
      }

      default:
        if (field?.isRadioButton) {
          return (
            <div
              id={field?.fieldName}
              className={formStyles?.radioSection}
              key={field?.fieldName + field?.helperText}
            >
              <h2 className={formStyles?.radioHeader}>{field?.fieldName}</h2>
              <div role="group">
                {field?.radioButtonOptions?.map((option: any) => (
                  <label key={option}>
                    <Field
                      type="radio"
                      name={field?.internalTitle}
                      value={option}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              {errors[field?.internalTitle] &&
                touched[field?.internalTitle] && (
                  <aside className={formStyles?.radioError}>
                    <span className={formStyles?.iconWrapper}>
                      <AlertIcon size="xsmall" />
                    </span>
                    <span>{errors[field?.internalTitle]}</span>
                  </aside>
                )}
            </div>
          )
        } else {
          const extraProps = {} as TextFieldProps

          if (field?.fieldName?.toLowerCase() === "email address") {
            extraProps.autoComplete = "email"
          }
          if (field?.fieldName?.toLowerCase() === "phone number") {
            extraProps.autoComplete = "tel"
          }

          return (
            <div ref={(el) => (fieldRefs.current[field.internalTitle] = el)}>
              <TextField
                {...extraProps}
                key={field?.fieldName + field?.helperText}
                id={field?.fieldName}
                name={field?.internalTitle}
                label={field?.fieldName}
                required={field?.isRequired}
                helperText={field?.helperText}
              />
            </div>
          )
        }
    }
  }
)

export const ContactForm: FC<Props> = ({
  handleBackBtnClick,
  setCaseId,
  setCallbackFormSubmitted,
  layoutProps,
  nodeLocale,
  contactForms,
  gatsbyPageProps,
  backCrumbTitle = "Contact Us"
}) => {
  const dispatch = useDispatch()
  const userProfile = useSelector((state: RootState) => state.userProfile)
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state.metaData.hasAttemptedLogin
  )
  const [formContent, setFormContent] = useState<ContactFormContent>()
  const [subtopics, setSubtopics] = useState<Options[]>()
  const [countryOfViolation, setCountryOfViolation] = useState<string>("")
  const [isEU, setIsEU] = useState<boolean>(false)
  const fieldRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const { domain, tld } = useMemo(() => getDomainInfo(), [])
  const formParameterValue = useMemo(() => getFormParameterValue(), [])

  const isCallbackForm = useMemo(() => {
    if (typeof window === "undefined") return false
    const queryParams = new URLSearchParams(window.location.search)
    return queryParams.get("form") === "rc"
  }, [])

  const callbackTopicOptions = useMemo<Options[]>(() => {
    const pageData =
      gatsbyPageProps?.data as Queries.CommonIssuesAndQuestionsQuery

    if (pageData?.contentfulRoutingTopic?.routingTopicChildItems) {
      return pageData.contentfulRoutingTopic.routingTopicChildItems.map(
        (topic: any) => ({
          label: topic.routingTopicSelection,
          value: topic.routingTopicSalesforceApiName
        })
      )
    }

    return CALLBACK_TOPIC_OPTIONS
  }, [gatsbyPageProps?.data])

  const formContentMemo = useMemo(() => {
    if (typeof window === "undefined") return null

    const queryParams = new URLSearchParams(window.location.search)
    const form = contactForms?.edges?.find(
      (form) =>
        form?.node?.contactFormUrl === queryParams.get("form") &&
        form?.node?.node_locale.toLowerCase() === nodeLocale.toLowerCase()
    )
    return form?.node
  }, [contactForms, nodeLocale])

  useEffect(() => {
    setFormContent(formContentMemo ?? undefined)
  }, [formContentMemo])

  const isGatedForm = useMemo(() => {
    if (!formContent?.contactFormUrl) return false

    const formUrl = formContent.contactFormUrl

    for (const formKey of Object.keys(FORM_ACCESS_CONFIG)) {
      if (
        formUrl === formKey ||
        formUrl.endsWith(`/${formKey}`) ||
        formUrl.endsWith(formKey)
      ) {
        return true
      }
    }

    return false
  }, [formContent?.contactFormUrl])

  const isAuthenticating = isGatedForm && !hasAttemptedLogin

  const buttonCopy: FormButtonMicroCopySet | null = useMemo(() => {
    if (!formContent?.contactFormButtons) return null
    const objMap = formContent.contactFormButtons.map((button) =>
      button?.microcopySetEntries?.map((entry) =>
        Object.values(entry as { [key: string]: MicroCopy })
      )
    )
    if (!objMap[0]) return null
    return Object.fromEntries(
      objMap[0] as Iterable<[string, MicroCopy]>
    ) as FormButtonMicroCopySet
  }, [formContent?.contactFormButtons])

  const callbackDisclaimerErrorMessage = useMemo(() => {
    const pageData =
      gatsbyPageProps?.data as Queries.CommonIssuesAndQuestionsQuery
    const agreeToTermsError = pageData?.agreeToTermsError

    if (agreeToTermsError?.errorMessageText) {
      return agreeToTermsError.errorMessageText
    }

    return ""
  }, [gatsbyPageProps?.data])

  const contactFormValidationSchema = useMemo(() => {
    if (!formContent) return object().shape({})

    const validationObject: Record<string, any> = {}

    if (isCallbackForm) {
      validationObject["Select a topic"] = string().required(
        "Please select a topic"
      )

      if (callbackDisclaimerErrorMessage) {
        validationObject["Privacy Policy Agreement"] = boolean().oneOf(
          [true],
          callbackDisclaimerErrorMessage
        )
      }
    }

    formContent.sections?.forEach((section) => {
      section?.fields?.forEach((field) => {
        // Skip fields without a usable key — Contentful can return null
        // for internalTitle, and using null as an object key coerces to
        // the literal string "null", producing a validation rule that
        // no real form field would ever match.
        if (!field?.internalTitle) return

        if (
          isCallbackForm &&
          (field.internalTitle.toLowerCase().includes("subject") ||
            field?.fieldName?.toLowerCase().includes("subject"))
        ) {
          return
        }
        if (
          isCallbackForm &&
          (field?.fieldName?.toLowerCase().includes("select a topic") ||
            field?.internalTitle?.toLowerCase().includes("select a topic"))
        ) {
          return
        }

        // Handle phone number validation for callback form
        if (field?.internalTitle === "Phone number (Request callback form)") {
          if (field?.isRequired && (field?.errorMessage?.length ?? 0) > 0) {
            const errorMessage =
              field?.errorMessage?.[0]?.errorMessageText ?? undefined

            // Validate the _raw field where the actual phone input is stored
            validationObject[`${field.internalTitle}_raw`] = string()
              .required(errorMessage)
              .test(
                "phone-format",
                "Please enter a valid phone number",
                (value) => {
                  if (!value) return true // Let required handle empty values
                  const cleanNumber = value.replace(/\D/g, "")
                  return cleanNumber.length >= 7 && cleanNumber.length <= 15
                }
              )
          }
        } else if (
          field?.isRequired &&
          (field?.errorMessage?.length ?? 0) > 0
        ) {
          const errorMessage =
            field?.errorMessage?.[0]?.errorMessageText ?? undefined

          if (field?.isRadioButton) {
            validationObject[field.internalTitle] =
              string().required(errorMessage)
          } else if (
            field?.fieldName?.trim()?.toLowerCase() === "email address" ||
            field?.fieldName?.trim()?.toLowerCase() === "organizer email"
          ) {
            validationObject[field.internalTitle] = string()
              .email(errorMessage)
              //implemented custom regex since the pre-defined yup validation doesn't seem to be working in some cases
              .matches(
                /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
                errorMessage
              )
              .required(errorMessage)
          } else if (
            field?.internalTitle?.trim()?.toLowerCase() ===
            "last 4 digits of card"
          ) {
            validationObject[field.internalTitle] = string()
              .length(4, errorMessage)
              .required(errorMessage)
          } else if (
            field?.internalTitle?.trim()?.toLowerCase() ===
              "date of purchase" ||
            field?.internalTitle?.trim()?.toLowerCase() === "order date"
          ) {
            validationObject[field.internalTitle] = string()
              .matches(/^\d{2}\/\d{2}\/\d{4}$/, errorMessage)
              .required(errorMessage)
          } else if (
            field?.internalTitle?.trim()?.toLowerCase() ===
            "routing topics - email"
          ) {
            const [topicHeader, subtopicHeader] = field.fieldName?.split(
              " -- "
            ) ?? ["", ""]
            validationObject[topicHeader] = string().required(errorMessage)
            validationObject[subtopicHeader] = string().required(errorMessage)
          } else if (
            field?.errorMessage?.some(
              (x) =>
                x?.errorMessageText?.trim()?.toLowerCase() ===
                "this field must be a date in yyyy-mm-dd format."
            )
          ) {
            const errMessage =
              field.errorMessage?.find(
                (x) =>
                  x?.errorMessageText?.toLowerCase() ===
                  "this field must be a date in yyyy-mm-dd format."
              )?.errorMessageText ||
              "This field must be a date in YYYY-MM-DD format."

            validationObject[field.internalTitle] = date()
              .required(errMessage)
              .typeError(errMessage)
              .transform((value, currentValue) => {
                const regex = /^\d{4}-\d{2}-\d{2}$/
                if (regex.test(currentValue)) {
                  return new Date(currentValue)
                }
                return NaN
              })
          } else if (
            field?.internalTitle
              ?.trim()
              ?.toLowerCase()
              .includes("dsa certify checkbox")
          ) {
            const errMessage =
              field.errorMessage?.find(
                (x) =>
                  x?.errorMessageText?.toLowerCase() ===
                  "this field is required."
              )?.errorMessageText || "This field is required."

            validationObject[field.internalTitle] = boolean().oneOf(
              isEU ? [true] : [true, false],
              errMessage
            )
          } else if (field?.internalTitle?.includes("Message")) {
            if (isCallbackForm) {
              validationObject[field.internalTitle] = string()
                .required("Description is required")
                .max(100, "Description must be 100 characters or less")
            } else {
              validationObject[field.internalTitle] =
                string().required(errorMessage)
            }
          } else {
            validationObject[field.internalTitle] =
              string().required(errorMessage)
          }
        }
      })
    })

    return object().shape(validationObject)
  }, [formContent, isEU, isCallbackForm, callbackDisclaimerErrorMessage])

  const initialFormValues = useMemo(() => {
    if (!formContent) return {}

    const initialValuesObject: Record<string, string | boolean> = {}

    formContent.sections?.forEach((section) => {
      section?.fields?.forEach((field) => {
        if (!field?.internalTitle) return

        if (
          isCallbackForm &&
          (field?.fieldName?.toLowerCase().includes("select a topic") ||
            field.internalTitle.toLowerCase().includes("select a topic"))
        ) {
          return
        }

        if (field.internalTitle === "Phone number (Request callback form)") {
          initialValuesObject[field.internalTitle] = ""
          initialValuesObject[`${field.internalTitle}_raw`] = ""
        } else if (
          field.internalTitle.trim().toLowerCase() === "routing topics - email"
        ) {
          const [topicHeader, subtopicHeader] = field.fieldName?.split(
            " -- "
          ) ?? ["", ""]
          initialValuesObject[topicHeader] = ""
          initialValuesObject[subtopicHeader] = ""
        } else if (
          field.internalTitle.trim().toLowerCase() ===
          "dsa certify checkbox (required)"
        ) {
          initialValuesObject[field.internalTitle] = false
        } else {
          initialValuesObject[field.internalTitle] = ""
        }
      })
    })

    if (isCallbackForm) {
      initialValuesObject["Select a topic"] = ""
      initialValuesObject["Privacy Policy Agreement"] = false
    }

    return initialValuesObject
  }, [formContent, isCallbackForm])

  const handleFormSubmit: FormikConfig<Values>["onSubmit"] = useCallback(
    async (values, { setSubmitting }) => {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 30000)

      try {
        setSubmitting(true)

        if (isCallbackForm) {
          const messageFieldValue =
            values["Message (required)"] ||
            values["Message (optional)"] ||
            values["Message (required) - DSA RTE"] ||
            ""

          const callbackPayload = {
            firstName: values["First name"] || "",
            lastName: values["Last name"] || "",
            email: values["Email address"] || "",
            phoneNumber: values["Phone number (Request callback form)"] || "",
            topic: values["Select a topic"] || "",
            eventUrl: values["Event URL (optional)"] || "",
            description: messageFieldValue
          }

          const response = await fetch(
            withPrefix("/api/salesforce/immediate-callback"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json"
              },
              body: JSON.stringify(callbackPayload),
              signal: controller.signal
            }
          )

          const responseData = await response.json()

          if (!response.ok) {
            throw new Error(
              responseData?.result?.error ||
                responseData?.error ||
                responseData?.message ||
                "Failed to submit callback request. Please try again."
            )
          }
          setSubmitting(false)

          // contact-us.tsx will handle showing the success alert
          if (setCallbackFormSubmitted) {
            setCallbackFormSubmitted(true)
          }

          // Remove query parameter when going back after successful submission
          handleBackBtnClick(true)
        } else {
          values.origin = "web"

          if (formContent?.contactFormSubjectLine) {
            values.Subject = formContent.contactFormSubjectLine
          }

          if (
            formContent?.contactFormUrl?.trim()?.toLowerCase() === "org-email"
          ) {
            values.contactType = "organizer"
          }

          values.slug = formContent?.contactFormUrl ?? ""

          const userProfileCopy: Partial<UserProfile> = {
            ...(userProfile || {})
          }
          if (userProfileCopy?.allData) {
            // @ts-expect-error intentionally scrubbing allData to empty
            userProfileCopy.allData = {}
          }

          const response = await fetch(
            withPrefix("/api/salesforce/email-case"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify({
                formValues: values,
                userProfile: userProfileCopy,
                localeData: { tld, domain, nodeLocale }
              }),
              signal: controller.signal
            }
          )

          const responseData: ApiResponse<any> = await response.json()

          if (!response.ok || !responseData?.result?.data?.CaseNumber) {
            const innerErrorMessage =
              typeof responseData?.result === "string"
                ? responseData.result
                : responseData?.result?.length > 0
                  ? responseData?.result[0]?.message
                  : "There was an error processing your form. Please try again."

            throw new Error(innerErrorMessage)
          }

          const caseIdValue = String(responseData.result.data.CaseNumber).trim()

          if (!caseIdValue || caseIdValue.length === 0) {
            throw new Error(
              "Invalid case number received. Please try again or contact support."
            )
          }

          setCaseId(caseIdValue)
          setSubmitting(false)
          // Remove query parameter when going back after successful submission
          handleBackBtnClick(true)
        }

        if (typeof window !== "undefined") {
          window.scroll(0, 0)
        }
      } catch (error: any) {
        console.error("Form submission error:", error)

        const errorMessage =
          error?.name === "AbortError"
            ? "Request timed out. Please check your connection and try again."
            : error?.message ||
              "An error occurred. Please try again or contact support."

        setSubmitting(false)

        dispatch(
          setInlineAlertProps({
            active: true,
            title: "Communication error",
            description: errorMessage,
            variant: "danger"
          })
        )

        if (typeof window !== "undefined") {
          window.scroll(0, 0)
        }
      } finally {
        clearTimeout(timeout)
      }
    },
    [
      formContent,
      userProfile,
      tld,
      domain,
      nodeLocale,
      setCaseId,
      setCallbackFormSubmitted,
      handleBackBtnClick,
      dispatch,
      isCallbackForm
    ]
  )

  const handleSelectMenuChange = useCallback<HandleTopicSelection>(
    (selection, options) => {
      const selectionObj = options.find(
        (topic) => topic.routingTopicSalesforceApiName === selection
      )
      setSubtopics(
        selectionObj?.routingTopicChildItems.map((sub) => ({
          label: sub.routingSubtopicSelection,
          value: sub.routingSubtopicSalesforceApiName
        }))
      )
    },
    []
  )

  const handleCountryOfOriginChange =
    useCallback<HandleCountryOfViolationSelection>((selection, euCountries) => {
      const isEuropean = euCountries.includes(selection)
      setIsEU(isEuropean)
      setCountryOfViolation(selection)
    }, [])

  if (!formContent) {
    return <div>Loading...</div>
  }

  return (
    <Layout
      contact={false}
      searchBar={{ isSearchBarVisible: true, isResultsBoxShown: true }}
      gatsbyPageProps={gatsbyPageProps}
      {...layoutProps}
    >
      <div>
        <div className={styles?.wrapper}>
          <div className={styles?.formContainer}>
            <BackCrumb
              title={backCrumbTitle}
              url="/contact-us"
              nodeLocale={nodeLocale}
            />
            <InlineAlert />

            <Stack space="spacing-md" className={formStyles.headerWrapper}>
              <Typography
                as="h2"
                variant="heading-md"
                className={formStyles.headerText}
              >
                {renderRichText(
                  formContent?.header?.value?.raw || "",
                  customRendererToRemovePTag
                )}
              </Typography>
              <Typography
                as="h5"
                variant="body-lg-bold"
                className={formStyles.subheaderText}
              >
                {renderRichText(
                  formContent?.subheader?.value?.raw || "",
                  customRendererToRemovePTag
                )}
              </Typography>
            </Stack>

            {isAuthenticating ? (
              <LoadingSpinner />
            ) : (
              <FormGating
                redirectUrl="/contact-us"
                formUrl={formContent?.contactFormUrl || ""}
              >
                <Formik
                  initialValues={initialFormValues}
                  validationSchema={contactFormValidationSchema}
                  validateOnBlur={false}
                  validateOnChange={true}
                  onSubmit={handleFormSubmit}
                >
                  {({
                    isSubmitting,
                    errors,
                    touched,
                    handleChange
                  }: FormikProps<Values>) => {
                    const hasScrolledToErrorRef = useRef<boolean>(false)
                    const prevIsSubmittingRef = useRef<boolean>(false)

                    useEffect(() => {
                      // Only scroll when submission transitions from false to true
                      const justStartedSubmitting =
                        !prevIsSubmittingRef.current && isSubmitting
                      if (justStartedSubmitting) {
                        hasScrolledToErrorRef.current = false
                      }

                      // Only run scroll logic once per submission attempt when errors exist
                      if (isSubmitting && !hasScrolledToErrorRef.current) {
                        const errorKeys = Object.keys(errors)
                        if (errorKeys.length > 0) {
                          hasScrolledToErrorRef.current = true
                          const nextErrorField = errorKeys.find(
                            (field) => fieldRefs.current[field]
                          )
                          if (nextErrorField) {
                            setTimeout(() => {
                              const element = fieldRefs.current[nextErrorField]
                              if (element) {
                                element.scrollIntoView({
                                  behavior: "smooth",
                                  block: "center"
                                })
                                element.focus?.()
                              }
                            }, 100)
                          }
                        }
                      }

                      prevIsSubmittingRef.current = isSubmitting
                    }, [errors, isSubmitting])

                    return (
                      <Form noValidate={true}>
                        <div className={formStyles.fieldWrapper}>
                          {formContent?.sections?.map((section) => (
                            <div
                              key={
                                section?.fields?.[0]?.fieldName ?? Math.random()
                              }
                            >
                              {section?.sectionHeader && (
                                <h2 className={formStyles?.sectionHeader}>
                                  {section.sectionHeader}
                                </h2>
                              )}
                              {section?.internalTitle
                                ?.trim()
                                .toLowerCase()
                                .includes("message") && (
                                <div>
                                  {formContent.contactFormSubjectLine &&
                                    !isCallbackForm && (
                                      <div>
                                        <h2
                                          className={formStyles?.subjectHeader}
                                        >
                                          Subject
                                        </h2>
                                        <h3 className={formStyles?.subjectLine}>
                                          {formContent.contactFormSubjectLine}
                                        </h3>
                                      </div>
                                    )}
                                </div>
                              )}
                              {section?.fields?.map((field) => {
                                if (
                                  isCallbackForm &&
                                  (field?.fieldName
                                    ?.toLowerCase()
                                    .includes("select a topic") ||
                                    field?.internalTitle
                                      ?.toLowerCase()
                                      .includes("select a topic"))
                                ) {
                                  return null
                                }

                                return (
                                  <FieldRenderer
                                    key={`${field?.fieldName}${field?.helperText}`}
                                    field={field}
                                    fieldRefs={fieldRefs}
                                    handleChange={handleChange}
                                    handleSelectMenuChange={
                                      handleSelectMenuChange
                                    }
                                    handleCountryOfOriginChange={
                                      handleCountryOfOriginChange
                                    }
                                    subtopics={subtopics}
                                    errors={errors}
                                    touched={touched}
                                    isCallbackForm={isCallbackForm}
                                    formContent={formContent}
                                    callbackTopicOptions={callbackTopicOptions}
                                    tld={tld}
                                    nodeLocale={nodeLocale}
                                  />
                                )
                              })}
                            </div>
                          ))}
                          {isCallbackForm && (
                            <div
                              ref={(el) =>
                                (fieldRefs.current["Privacy Policy Agreement"] =
                                  el)
                              }
                            >
                              <Checkbox
                                id="privacy-policy-agreement"
                                name="Privacy Policy Agreement"
                                labelText={(() => {
                                  const disclaimerEntry =
                                    formContent?.Disclaimers?.find(
                                      (disclaimer) =>
                                        disclaimer?.microcopyKey ===
                                        "formDisclaimerCallRecording"
                                    )

                                  if (disclaimerEntry?.microcopyValue?.raw) {
                                    return renderEnhancedRichText(
                                      disclaimerEntry.microcopyValue.raw,
                                      customRendererToRemovePTag
                                    )
                                  }

                                  return ""
                                })()}
                              />
                              {errors["Privacy Policy Agreement"] &&
                                touched["Privacy Policy Agreement"] &&
                                callbackDisclaimerErrorMessage && (
                                  <aside className={formStyles.radioError}>
                                    <span className={formStyles.iconWrapper}>
                                      <AlertIcon size="xsmall" />
                                    </span>
                                    <span>
                                      {callbackDisclaimerErrorMessage}
                                    </span>
                                  </aside>
                                )}
                            </div>
                          )}
                        </div>

                        {!isCallbackForm && (
                          <div className={styles?.formFooterContainer}>
                            {formContent?.Disclaimers?.map((disclaimer) => (
                              <div
                                key={`${disclaimer?.microcopyKey}${disclaimer?.microcopyValue}`}
                              >
                                {renderRichText(
                                  disclaimer?.microcopyValue?.raw ?? undefined
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        <div className={styles?.buttonContainer}>
                          <Button
                            size="responsive"
                            className={styles?.backButton}
                            onClick={() => handleBackBtnClick()}
                            disabled={isSubmitting}
                          >
                            {renderRichText(
                              buttonCopy?.buttonBack?.raw,
                              customRendererToRemovePTag
                            )}
                          </Button>
                          <Button
                            size="responsive"
                            variant="primary"
                            type="submit"
                            disabled={isSubmitting}
                          >
                            {renderRichText(
                              buttonCopy?.buttonSend?.raw,
                              customRendererToRemovePTag
                            )}
                          </Button>
                        </div>
                        <CustomValidation />
                      </Form>
                    )
                  }}
                </Formik>
              </FormGating>
            )}
          </div>

          <div
            className={styles.bgContainer}
            style={{
              backgroundImage: `url(${formContent?.contactFormGraphic?.url})`
            }}
          />
        </div>
      </div>
    </Layout>
  )
}
